import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";
import { Save, Building, Link, Palette, Mail, CreditCard, ExternalLink, Upload, X, Loader2, Gift, Trash2, AlertTriangle, Download, Shield } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import type { Business } from "@shared/schema";
import { getPlanLimits, isAppSumoPlan } from "@shared/plans";
import { fetchWithAuth } from "@/lib/api";

const settingsSchema = z.object({
  name: z.string().min(2, "Business name is required"),
  slug: z.string().min(2, "Slug is required"),
  ownerEmail: z.string().email("Valid email required"),
  googleReviewUrl: z.string().url("Valid URL required").optional().or(z.literal("")),
  brandColor: z.string(),
  emailTemplate: z.string().optional(),
  logoUrl: z.string().optional().nullable(),
  showLogoInEmail: z.boolean().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

export default function Settings() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const { data: business, isLoading } = useQuery<Business>({
    queryKey: ["/api/business"],
  });

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: "",
      slug: "",
      ownerEmail: "",
      googleReviewUrl: "",
      brandColor: "#3B82F6",
      emailTemplate: "",
      logoUrl: "",
      showLogoInEmail: false,
    },
  });

  useEffect(() => {
    if (business) {
      form.reset({
        name: business.name,
        slug: business.slug,
        ownerEmail: business.ownerEmail,
        googleReviewUrl: business.googleReviewUrl || "",
        brandColor: business.brandColor || "#3B82F6",
        emailTemplate: business.emailTemplate || "",
        logoUrl: business.logoUrl || "",
        showLogoInEmail: business.showLogoInEmail || false,
      });
      setLogoPreview(business.logoUrl || null);
    }
  }, [business, form]);

  // Upload logo to Supabase Storage
  const uploadLogo = async (file: File): Promise<string | null> => {
    if (!business?.id) return null;
    
    try {
      setIsUploadingLogo(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${business.id}/logo-${Date.now()}.${fileExt}`;
      
      const { error } = await supabase.storage
        .from('business-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) {
        console.error('Logo upload error:', error);
        toast({
          title: "Upload failed",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('business-logos')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast({
        title: "Upload failed",
        description: "Failed to upload logo",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Logo must be under 2MB",
        variant: "destructive",
      });
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Supabase
    const logoUrl = await uploadLogo(file);
    if (logoUrl) {
      form.setValue('logoUrl', logoUrl);
      toast({
        title: "Logo uploaded",
        description: "Don't forget to save your settings!",
      });
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    form.setValue('logoUrl', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateMutation = useMutation({
    mutationFn: async (data: SettingsFormData) => {
      return apiRequest("PATCH", "/api/business", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business"] });
      toast({
        title: "Settings saved",
        description: "Your changes have been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SettingsFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure your business profile and review settings
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Business Information
              </CardTitle>
              <CardDescription>
                Your business details as shown to customers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Name</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-business-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Review Page Slug</FormLabel>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {window.location.origin}/r/
                      </span>
                      <FormControl>
                        <Input {...field} className="font-mono" data-testid="input-slug" />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ownerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} data-testid="input-owner-email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-5 w-5" />
                Google Review Integration
              </CardTitle>
              <CardDescription>
                Connect to your Google Business Profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="googleReviewUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Google Review URL</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="https://g.page/r/your-business/review"
                        data-testid="input-google-url"
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Find this in your Google Business Profile under "Get more reviews"
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Branding
              </CardTitle>
              <CardDescription>
                Customize the look of your review pages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="brandColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Brand Color</FormLabel>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={field.value}
                        onChange={field.onChange}
                        className="h-10 w-16 rounded-md border cursor-pointer"
                        data-testid="input-brand-color"
                      />
                      <FormControl>
                        <Input {...field} className="font-mono max-w-32" />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-2">
                <FormLabel>Logo</FormLabel>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                  data-testid="input-logo-file"
                />
                {logoPreview ? (
                  <div className="border rounded-md p-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={logoPreview}
                        alt="Business logo"
                        className="h-20 w-20 object-contain rounded-md border bg-muted"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Current Logo</p>
                        <p className="text-xs text-muted-foreground">Click below to change</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        onClick={removeLogo}
                        data-testid="button-remove-logo"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      className="mt-3 w-full"
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      data-testid="button-change-logo"
                    >
                      {isUploadingLogo ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Change Logo
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed rounded-md p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isUploadingLogo ? (
                      <>
                        <Loader2 className="h-8 w-8 mx-auto text-muted-foreground mb-2 animate-spin" />
                        <p className="text-sm text-muted-foreground">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Click to upload your logo
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PNG, JPG, GIF, WebP or SVG (max 2MB)
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Show logo in email checkbox - only visible when logo is uploaded */}
              {logoPreview && (
                <FormField
                  control={form.control}
                  name="showLogoInEmail"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-show-logo-email"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="cursor-pointer">
                          Show logo in review request emails
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">
                          Your logo will appear at the top of emails sent to customers
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Template
              </CardTitle>
              <CardDescription>
                Customize the review request email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="emailTemplate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Body</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={10}
                        className="font-mono text-sm"
                        placeholder={`Hi {{customer_name}},

Thank you for choosing {{business_name}}! We hope you were satisfied with the service provided by {{staff_name}}.

We'd greatly appreciate it if you could take a moment to share your experience.

Best regards,
{{business_name}}`}
                        data-testid="textarea-email-template"
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Available variables: {`{{customer_name}}`}, {`{{staff_name}}`},{" "}
                      {`{{business_name}}`}, {`{{job_description}}`}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={updateMutation.isPending} data-testid="button-save-settings">
              <Save className="h-4 w-4 mr-2" />
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>

      <AppSumoSection />
      <BillingSection business={business} />
      <DataPrivacySection />
      <DangerZoneSection />
    </div>
  );
}

interface AppSumoLicenseResponse {
  hasLicense: boolean;
  tier?: number;
  status?: string;
  activatedAt?: string;
}

function AppSumoSection() {
  const { data: license, isLoading } = useQuery<AppSumoLicenseResponse>({
    queryKey: ["/api/appsumo/license"],
    queryFn: () => fetchWithAuth("/api/appsumo/license"),
  });

  if (isLoading || !license?.hasLicense) return null;

  return (
    <Card className="mt-6" data-testid="card-appsumo">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5" />
          AppSumo License
        </CardTitle>
        <CardDescription>
          Your lifetime deal from AppSumo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <Badge variant="secondary">Tier {license.tier}</Badge>
            {license.activatedAt && (
              <p className="text-sm text-muted-foreground mt-1">
                Activated {format(new Date(license.activatedAt), "MMM d, yyyy")}
              </p>
            )}
          </div>
          <Badge variant={license.status === "active" ? "default" : "destructive"}>
            {license.status}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

function BillingSection({ business }: { business: Business | undefined }) {
  const { toast } = useToast();
  const planType = business?.planType || "free";
  const limits = getPlanLimits(planType);
  const monthlyRequestCount = business?.monthlyRequestCount || 0;

  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/stripe/portal", {});
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to open billing portal",
        variant: "destructive",
      });
    },
  });

  const getPlanBadgeVariant = () => {
    if (planType === "pro") return "default";
    if (planType === "lifetime") return "secondary";
    if (isAppSumoPlan(planType)) return "secondary";
    return "outline";
  };

  const getPlanDisplayName = () => {
    if (planType === "pro") return "Pro";
    if (planType === "lifetime") return "Lifetime";
    if (planType === "pro_cancelled") return "Pro (Cancelled)";
    if (planType === "appsumo_tier1") return "AppSumo Tier 1";
    if (planType === "appsumo_tier2") return "AppSumo Tier 2";
    if (planType === "appsumo_tier3") return "AppSumo Tier 3";
    return "Free";
  };

  return (
    <Card className="mt-6" data-testid="card-billing">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Billing & Plan
        </CardTitle>
        <CardDescription>
          Manage your subscription and usage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Current Plan</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={getPlanBadgeVariant()} data-testid="badge-plan-type">
                {getPlanDisplayName()}
              </Badge>
            </div>
          </div>
          {planType === "free" && (
            <Button variant="outline" asChild data-testid="button-upgrade">
              <a href="/pricing">
                Upgrade
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Requests this month</span>
            <span data-testid="text-usage">
              {monthlyRequestCount} / {limits.maxRequestsPerMonth === Infinity ? "Unlimited" : limits.maxRequestsPerMonth}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Staff limit</span>
            <span data-testid="text-staff-limit">
              {limits.maxStaff === Infinity ? "Unlimited" : limits.maxStaff}
            </span>
          </div>
        </div>

        {(planType === "pro" || planType === "lifetime" || planType === "pro_cancelled") && business?.stripeCustomerId && (
          <Button
            variant="outline"
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending}
            data-testid="button-manage-billing"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {portalMutation.isPending ? "Loading..." : "Manage Billing"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function DataPrivacySection() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);

    try {
      const response = await fetch("/api/business/export", {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || "Failed to export data");
      }

      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "reviewkit-data-export.json";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Data exported",
        description: "Your data has been downloaded as a JSON file.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : "Failed to export data",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="card-data-privacy">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Data & Privacy
        </CardTitle>
        <CardDescription>
          Manage your data and privacy settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Export Your Data</p>
            <p className="text-sm text-muted-foreground">
              Download all your data in a portable JSON format (GDPR compliant)
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExportData}
            disabled={isExporting}
            data-testid="button-export-data"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export Data
              </>
            )}
          </Button>
        </div>
        <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3">
          <p>Your export includes: business profile, staff members, review requests, customer feedback, and account information.</p>
        </div>
      </CardContent>
    </Card>
  );
}

function DangerZoneSection() {
  const { toast } = useToast();
  const { signOut, session } = useAuth();
  const [, setLocation] = useLocation();
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isConfirmValid = confirmText === "DELETE MY ACCOUNT";

  const handleDeleteAccount = async () => {
    if (!isConfirmValid) return;

    setIsDeleting(true);

    try {
      const response = await fetch("/api/auth/account", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ confirmation: "DELETE MY ACCOUNT" }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || "Failed to delete account");
      }

      toast({
        title: "Account deleted",
        description: "Your account and all data have been permanently deleted.",
      });

      // Sign out and redirect to home
      await signOut();
      setLocation("/");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete account",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDialogOpen(false);
    }
  };

  return (
    <Card className="mt-6 border-destructive/50" data-testid="card-danger-zone">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          Danger Zone
        </CardTitle>
        <CardDescription>
          Irreversible actions that affect your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Delete Account</p>
            <p className="text-sm text-muted-foreground">
              Permanently delete your account and all associated data
            </p>
          </div>
          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="button-delete-account">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Delete your account?
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-3">
                  <p>
                    This action <strong>cannot be undone</strong>. This will permanently delete:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Your account and login credentials</li>
                    <li>Your business profile</li>
                    <li>All staff members</li>
                    <li>All review requests and history</li>
                    <li>All customer feedback data</li>
                  </ul>
                  <div className="pt-2">
                    <Label htmlFor="confirm-delete" className="text-foreground">
                      Type <strong>DELETE MY ACCOUNT</strong> to confirm:
                    </Label>
                    <Input
                      id="confirm-delete"
                      className="mt-2"
                      placeholder="DELETE MY ACCOUNT"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      disabled={isDeleting}
                      data-testid="input-confirm-delete"
                    />
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={!isConfirmValid || isDeleting}
                  data-testid="button-confirm-delete"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete my account"
                  )}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
